import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './CountDownText.css';

dayjs.extend(durationPlugin);

type CountDownTextProps = {
  timestamp: number; // Future timestamp in milliseconds
  label?: string;
  className?: string;
};

export const CountDownText = memo(
  ({ timestamp, label, className }: CountDownTextProps) => {
    const { t } = useTranslation();
    const calculateTimeLeft = useCallback(() => {
      const difference = timestamp - Date.now();
      return difference > 0 ? difference : 0;
    }, [timestamp]);

    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

    useEffect(() => {
      const timer = setInterval(() => {
        setTimeLeft(calculateTimeLeft());
      }, 1000);
      return () => clearInterval(timer);
    }, [calculateTimeLeft, timestamp]);

    const formatTimeLeft = (milliseconds: number) => {
      const durationObj = dayjs.duration(milliseconds);
      const days = Math.floor(durationObj.asDays());
      const hours = String(durationObj.hours()).padStart(2, '0');
      const minutes = String(durationObj.minutes()).padStart(2, '0');
      const seconds = String(durationObj.seconds()).padStart(2, '0');

      return days > 0
        ? t('{days} days {hours}:{minutes}:{seconds}', {
            days: days,
            hours: hours,
            minutes: minutes,
            seconds: seconds,
          })
        : `${hours}:${minutes}:${seconds}`;
    };

    const timeString = formatTimeLeft(timeLeft);

    return (
      <p className={className}>
        {label} {timeString}
      </p>
    );
  },
);
